import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { TextField, Grid2 as Grid, Button, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import CustomerSelect from '@components/customer/CustomerSelect';
import SendoutStatusSelect from '@components/sendout/SendoutStatusSelect';
import WorkflowSelect from '@components/workflow/WorkflowSelect';
import MailingListSelect from '@components/mailinglist/MailingListSelect';

const SendoutSearch = ({ filter = {}, handleSubmit }) => {
    const { t } = useTranslation();
    const { control, handleSubmit: handleFormSubmit, reset } = useForm();
    const { isSuperAdmin } = useAuth();

    const onSubmit = (data) => {
        handleSubmit(data);
    };

    const resetForm = () => {
        reset({
            description: '',
            status: [],
            workflow_ids: [],
            mailing_list_ids: [],
            customer_id: '',
        });
        handleSubmit({});
    };

    return (
        <form onSubmit={handleFormSubmit(onSubmit)}>
            <Typography variant="h4" component="h4" sx={{ px: 2, pt: 2 }}>{t('Search|noun')}</Typography>
            <Grid container spacing={2} sx={{ px: 2, pb: 3 }}>
                <Grid size={{ xs: 12, sm: 2 }}>
                    <Controller
                        name="description"
                        control={control}
                        defaultValue={filter.description || ''}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                InputProps={{ type: 'search' }}
                                label={t('Description')}
                                variant="standard"
                                fullWidth
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                    <Controller
                        name="status"
                        control={control}
                        defaultValue={filter.status || []}
                        render={({ field }) => (
                            <FormControl
                                variant="standard"
                                fullWidth>
                                <InputLabel id="status-label">{t('Status')}</InputLabel>
                                <SendoutStatusSelect
                                    labelId="status-label"
                                    fullWidth
                                    multiple
                                    {...field}
                                />
                            </FormControl>
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                    <Controller
                        name="is_test"
                        control={control}
                        defaultValue={filter.is_test || ''}
                        render={({ field }) => (
                            <FormControl
                                variant="standard"
                                fullWidth>
                                <InputLabel id="is-test-label">{t('Type')}</InputLabel>
                                <Select {...field}>
                                    <MenuItem value="">{t('- Display all -')}</MenuItem>
                                    <MenuItem value="1">{t('Test mode')}</MenuItem>
                                    <MenuItem value="0">{t('Real sendout')}</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Controller
                        name="workflow_ids"
                        control={control}
                        defaultValue={filter.workflow_ids || []}
                        render={({ field }) => (
                            <FormControl
                                variant="standard"
                                fullWidth>
                                <InputLabel id="workflow-label">{t('Workflow')}</InputLabel>
                                <WorkflowSelect
                                    emptyLabel={t('- Display all -')}
                                    labelId="workflow-label"
                                    fullWidth
                                    multiple
                                    {...field}
                                />
                            </FormControl>
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Controller
                        name="mailing_list_ids"
                        control={control}
                        defaultValue={filter.mailing_list_ids || []}
                        render={({ field }) => (
                            <FormControl
                                variant="standard"
                                fullWidth>
                                <InputLabel id="mailing-list-label">{t('Mailing list')}</InputLabel>
                                <MailingListSelect
                                    emptyLabel={t('- Display all -')}
                                    labelId="mailing-list-label"
                                    fullWidth
                                    multiple
                                    {...field}
                                />
                            </FormControl>
                        )}
                    />
                </Grid>
                {isSuperAdmin() && (
                    <Grid size={{ xs: 12, sm: 2 }}>
                        <Controller
                            name="customer_id"
                            control={control}
                            defaultValue={filter.customer_id || ''}
                            render={({ field }) => (
                                <FormControl
                                    variant="standard"
                                    fullWidth>
                                    <InputLabel id="customer-label">{t('Customer')}</InputLabel>
                                    <CustomerSelect
                                        emptyLabel={t('- Display all -')}
                                        labelId="customer-label"
                                        fullWidth
                                        {...field}
                                    />
                                </FormControl>
                            )}
                        />
                    </Grid>
                )}
                <Grid size={{ xs: 12, sm: 3 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button type="submit" variant="contained" size="small" startIcon={<SearchIcon />} sx={{ mr: 1 }}>
                        {t('Search|verb')}
                    </Button>
                    <Button variant="outlined" size="small" onClick={resetForm} startIcon={<ClearIcon />}>
                        {t('Clear')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

export default SendoutSearch;
